import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

interface PaletteItem {
  id: string;
  label: string;
  description?: string;
  icon: string;
  category: string;
  action: () => void;
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

const RECENT_KEY = "cmd_palette_recent";

const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const [recentIds, setRecentIds] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); }
    catch { return []; }
  });

  const allItems = useMemo<PaletteItem[]>(() => [
    { id: "dashboard",    label: "Dashboard",           description: "Main overview",             icon: "🏠", category: "Modules",  action: () => navigate("/") },
    { id: "crm",          label: "CRM",                 description: "Lead Intelligence",         icon: "👥", category: "Modules",  action: () => navigate("/crm") },
    { id: "whatsapp",     label: "WhatsApp",            description: "Customer conversations",    icon: "💬", category: "Modules",  action: () => navigate("/whatsapp") },
    { id: "inventory",    label: "Inventory",           description: "Stock levels & alerts",     icon: "📦", category: "Modules",  action: () => navigate("/inventory") },
    { id: "pricing",      label: "Price Intelligence",  description: "Competitor pricing & AI",   icon: "💰", category: "Modules",  action: () => navigate("/pricing") },
    { id: "marketplace",  label: "Marketplace",         description: "Seller management",         icon: "🛒", category: "Modules",  action: () => navigate("/marketplace") },
    { id: "attendance",   label: "Attendance",          description: "Employee KPIs & check-in",  icon: "📋", category: "Modules",  action: () => navigate("/attendance") },
    { id: "seo",          label: "SEO Dashboard",       description: "Search optimisation",       icon: "🔍", category: "Modules",  action: () => navigate("/seo") },
    { id: "voice-sales",  label: "Voice Sales",         description: "AI voice calls",            icon: "🎙️", category: "Modules",  action: () => navigate("/voice-sales") },
    { id: "new-lead",     label: "New CRM Lead",        description: "Open the new lead form",    icon: "➕", category: "Actions",  action: () => navigate("/crm") },
    { id: "att-dash",     label: "Attendance Dashboard",description: "View KPI summary",          icon: "📊", category: "Actions",  action: () => navigate("/attendance/dashboard") },
    { id: "run-scan",     label: "Price Scan",          description: "Trigger competitor scan",   icon: "🔄", category: "Actions",  action: () => navigate("/pricing") },
  ], [navigate]);

  const filtered = useMemo(() => {
    if (!query.trim()) return allItems;
    const q = query.toLowerCase();
    return allItems.filter(
      item =>
        item.label.toLowerCase().includes(q) ||
        (item.description || "").toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q)
    );
  }, [query, allItems]);

  const displayed = useMemo(() => {
    if (query.trim()) return filtered;
    const recent = recentIds
      .map(id => allItems.find(i => i.id === id))
      .filter(Boolean) as PaletteItem[];
    const rest = allItems.filter(i => !recentIds.includes(i.id));
    return [...recent, ...rest];
  }, [query, filtered, allItems, recentIds]);

  const grouped = useMemo(() => {
    const map = new Map<string, PaletteItem[]>();
    displayed.forEach(item => {
      const key = !query.trim() && recentIds.includes(item.id) ? "Recent" : item.category;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    });
    return map;
  }, [displayed, query, recentIds]);

  const flatList = useMemo(() => Array.from(grouped.values()).flat(), [grouped]);

  const handleSelect = (item: PaletteItem) => {
    const updated = [item.id, ...recentIds.filter(id => id !== item.id)].slice(0, 5);
    setRecentIds(updated);
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
    item.action();
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => setSelected(0), [query]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowDown") { e.preventDefault(); setSelected(s => Math.min(s + 1, flatList.length - 1)); }
      if (e.key === "ArrowUp")   { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)); }
      if (e.key === "Enter" && flatList[selected]) handleSelect(flatList[selected]);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, flatList, selected, onClose]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${selected}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  if (!isOpen) return null;

  let flatIdx = 0;

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1100, backdropFilter: "blur(4px)" }} />
      <div style={{
        position: "fixed", top: "20%", left: "50%", transform: "translateX(-50%)",
        width: "min(580px, 90vw)",
        background: "linear-gradient(180deg, #111827 0%, #0b1326 100%)",
        border: "1px solid rgba(255,214,165,0.2)", borderRadius: 16,
        zIndex: 1101, overflow: "hidden",
        boxShadow: "0 40px 100px rgba(0,0,0,0.8)",
        fontFamily: "'Manrope', 'Space Grotesk', sans-serif",
        animation: "cmdFadeIn 0.15s ease-out",
      }}>
        <style>{`@keyframes cmdFadeIn { from { opacity:0; transform:translateX(-50%) scale(0.96); } to { opacity:1; transform:translateX(-50%) scale(1); } }`}</style>

        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid rgba(255,214,165,0.1)" }}>
          <span style={{ fontSize: 16, color: "#6b7280", flexShrink: 0 }}>🔍</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search modules, actions…"
            style={{ flex: 1, background: "none", border: "none", outline: "none", color: "#ffd6a5", fontSize: 15, fontFamily: "inherit" }}
          />
          <kbd style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 5, padding: "2px 7px", fontSize: 11, color: "#6b7280" }}>Esc</kbd>
        </div>

        <div ref={listRef} style={{ maxHeight: 360, overflowY: "auto", padding: "8px 0" }}>
          {flatList.length === 0 ? (
            <div style={{ padding: "24px 16px", textAlign: "center", color: "#4a5568", fontSize: 13 }}>No results for "{query}"</div>
          ) : (
            Array.from(grouped.entries()).map(([category, items]) => (
              <div key={category}>
                <div style={{ padding: "6px 16px 3px", fontSize: 10, color: "#4a5568", textTransform: "uppercase", letterSpacing: "0.08em" }}>{category}</div>
                {items.map(item => {
                  const idx = flatIdx++;
                  const isSel = idx === selected;
                  return (
                    <div key={item.id} data-idx={idx}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setSelected(idx)}
                      style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", cursor: "pointer",
                        background: isSel ? "rgba(255,214,165,0.1)" : "transparent",
                        borderLeft: isSel ? "2px solid #ffd6a5" : "2px solid transparent",
                        transition: "background 0.1s",
                      }}
                    >
                      <span style={{ fontSize: 17, flexShrink: 0, width: 24 }}>{item.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: isSel ? "#ffd6a5" : "#dae2fd" }}>{item.label}</div>
                        {item.description && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 1 }}>{item.description}</div>}
                      </div>
                      {isSel && <kbd style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 5, padding: "2px 7px", fontSize: 10, color: "#9b8f83", flexShrink: 0 }}>↵</kbd>}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div style={{ display: "flex", gap: 16, padding: "8px 16px", borderTop: "1px solid rgba(255,255,255,0.05)", fontSize: 11, color: "#4a5568" }}>
          <span><kbd style={{ background: "rgba(255,255,255,0.06)", borderRadius: 3, padding: "1px 5px", fontSize: 10 }}>↑↓</kbd> navigate</span>
          <span><kbd style={{ background: "rgba(255,255,255,0.06)", borderRadius: 3, padding: "1px 5px", fontSize: 10 }}>↵</kbd> open</span>
          <span><kbd style={{ background: "rgba(255,255,255,0.06)", borderRadius: 3, padding: "1px 5px", fontSize: 10 }}>Esc</kbd> close</span>
        </div>
      </div>
    </>
  );
};

export default CommandPalette;
